import type { Participant } from '@/types/tournament';

/**
 * Resolve a tournament participant's display name from the Laravel profile.
 *
 * The Laravel API sometimes returns the literal string "unknown" (or an empty
 * value) for the game nickname when a player has no game profile for that game
 * — that's what surfaced as "unknown" in the match/bracket screens. We treat
 * those as missing and fall back through the available name sources.
 *
 * Returns `null` when no usable name exists, so callers can render their own
 * placeholder (e.g. "Waiting for opponent…" / "TBD" / a generic label).
 */
export function resolvePlayerName(
  participant: Participant | null | undefined,
): string | null {
  if (!participant) return null;
  const candidates = [
    participant.profile?.info?.nickname,
    participant.profile?.user?.name,
    participant.profile?.user?.nickname,
  ];
  for (const c of candidates) {
    const v = typeof c === 'string' ? c.trim() : '';
    if (v && v.toLowerCase() !== 'unknown') return v;
  }
  return null;
}
